import React, { useState, useEffect, useRef } from 'react'
import { hairdresserActions } from '../../../_actions'

/**
 * Shared hand-pick-clients control for the email and SMS composers.
 * Search, paginated loading (50 a page with Load more), select-all for the
 * loaded eligible clients, and eligibility by channel: clients without an
 * email address (or phone number for SMS) are visible but not selectable.
 *
 * Props:
 *   dispatch      redux dispatch
 *   channel       'email' | 'sms' (drives the contact line + eligibility)
 *   selected      [{ id, name, email, phone }]
 *   setSelected   setter for the selection array
 */

const PAGE_SIZE = 50
const ink = '#1A1A1E'
const muted = '#8E8E93'
const line = '#ECECF0'

export const ClientPicker = ({ dispatch, channel = 'email', selected, setSelected }) => {
  const [search, setSearch] = useState('')
  const [items, setItems] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const aliveRef = useRef(true)

  useEffect(() => () => { aliveRef.current = false }, [])

  const fetchPage = (pageNum, append) => {
    return dispatch(hairdresserActions.getHairdresserClient({
      searchKey: search,
      page: pageNum,
      limit: PAGE_SIZE,
      // The action's catch handler navigates via params.history; give it a
      // no-op so a failed request cannot hijack routing from inside a wizard
      history: { push: () => {} },
    }))
      .then(res => {
        if (!aliveRef.current) return
        const data = res?.data?.responseData || res?.data?.data || {}
        const fetched = (data.clients || [])
          .filter(c => c.name && c.name !== 'Unknown')
          .map(c => ({ id: c.userId || c._id, name: c.name, email: c.email || '', phone: c.phoneNumber || '' }))
        setItems(prev => (append && prev ? [...prev, ...fetched] : fetched))
        setHasMore(fetched.length === PAGE_SIZE)
        setPage(pageNum)
      })
      .catch(() => { if (aliveRef.current) { setItems(prev => (append ? prev : [])); setHasMore(false) } })
  }

  // Initial load + debounced search
  useEffect(() => {
    setLoading(true)
    const timer = setTimeout(() => {
      fetchPage(1, false).then(() => { if (aliveRef.current) setLoading(false) })
    }, search ? 250 : 0)
    return () => clearTimeout(timer)
  }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  const contactOf = (c) => (channel === 'sms' ? c.phone : c.email)
  const missingLabel = channel === 'sms' ? 'No phone number' : 'No email address'
  const eligible = (items || []).filter(c => contactOf(c))
  const selectedIds = new Set(selected.map(c => c.id))
  const allLoadedSelected = eligible.length > 0 && eligible.every(c => selectedIds.has(c.id))

  const toggle = (client) => {
    setSelected(prev => (
      prev.some(c => c.id === client.id) ? prev.filter(c => c.id !== client.id) : [...prev, client]
    ))
  }

  const selectAllLoaded = () => {
    setSelected(prev => {
      const ids = new Set(prev.map(c => c.id))
      return [...prev, ...eligible.filter(c => !ids.has(c.id))]
    })
  }

  const deselectAllLoaded = () => {
    const loadedIds = new Set(eligible.map(c => c.id))
    setSelected(prev => prev.filter(c => !loadedIds.has(c.id)))
  }

  return (
    <div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search clients by name..."
        style={{
          width: '100%', height: 46, padding: '0 16px', boxSizing: 'border-box',
          border: `1px solid ${line}`, borderRadius: 12, fontSize: 14,
          fontFamily: 'inherit', outline: 'none', marginBottom: 12
        }}
      />

      {/* Toolbar: counts + bulk actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: selected.length ? '#007EA0' : muted }}>
          {selected.length
            ? `${selected.length} ${selected.length === 1 ? 'client' : 'clients'} selected`
            : items ? `${items.length} ${items.length === 1 ? 'client' : 'clients'}${hasMore ? ' loaded' : ''}` : ' '}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          {eligible.length > 0 && (
            <button
              type="button"
              onClick={allLoadedSelected ? deselectAllLoaded : selectAllLoaded}
              style={{ padding: '7px 13px', borderRadius: 100, border: `1px solid ${line}`, background: '#fff', fontSize: 12.5, fontWeight: 600, color: '#4B4B52', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {allLoadedSelected ? 'Deselect all' : `Select all${hasMore ? ' loaded' : ''}`}
            </button>
          )}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => setSelected([])}
              style={{ padding: '7px 13px', borderRadius: 100, border: 'none', background: 'rgba(0,126,160,0.08)', fontSize: 12.5, fontWeight: 600, color: '#007EA0', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div style={{
        border: `1px solid ${line}`, borderRadius: 14, overflow: 'hidden',
        height: 'calc(100vh - 360px)', minHeight: 420, overflowY: 'auto', backgroundColor: '#fff'
      }}>
        {loading ? (
          <div style={{ padding: 12 }}>
            {[1, 2, 3, 4, 5, 6, 7].map(i => (
              <div key={i} style={{ height: 52, borderRadius: 10, backgroundColor: '#F2F2F7', marginBottom: 8 }} />
            ))}
          </div>
        ) : !items || items.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <p style={{ fontSize: 13.5, fontWeight: 600, color: '#4B4B52', margin: '0 0 4px' }}>
              {search ? 'No clients match that search' : 'No clients yet'}
            </p>
            <p style={{ fontSize: 12.5, color: muted, margin: 0 }}>
              {search ? 'Try a different name.' : 'Add clients to your salon to send campaigns.'}
            </p>
          </div>
        ) : (
          <>
            {items.map(client => {
              const isSelected = selectedIds.has(client.id)
              const noContact = !contactOf(client)
              return (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => { if (!noContact) toggle(client) }}
                  disabled={noContact}
                  title={noContact ? `This client has ${missingLabel.toLowerCase()}` : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 13, width: '100%',
                    padding: '13px 16px', border: 'none', borderBottom: '1px solid #F2F2F7',
                    backgroundColor: isSelected ? 'rgba(0,126,160,0.05)' : '#fff',
                    cursor: noContact ? 'default' : 'pointer', fontFamily: 'inherit',
                    opacity: noContact ? 0.5 : 1, textAlign: 'left'
                  }}
                >
                  <span style={{
                    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: isSelected ? 'rgba(0,126,160,0.12)' : '#F2F2F7',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 600, color: isSelected ? '#007EA0' : muted
                  }}>
                    {client.name.charAt(0).toUpperCase()}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {contactOf(client) || missingLabel}
                    </span>
                  </span>
                  <span style={{
                    width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                    border: isSelected ? 'none' : '2px solid #DEDEE2',
                    backgroundColor: isSelected ? '#29282D' : '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    )}
                  </span>
                </button>
              )
            })}
            {hasMore && (
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => {
                  setLoadingMore(true)
                  fetchPage(page + 1, true).then(() => { if (aliveRef.current) setLoadingMore(false) })
                }}
                style={{
                  display: 'block', width: '100%', padding: '13px', border: 'none',
                  background: '#fafafa', fontSize: 13, fontWeight: 600, color: '#007EA0',
                  cursor: loadingMore ? 'default' : 'pointer', fontFamily: 'inherit'
                }}
              >
                {loadingMore ? 'Loading…' : 'Load 50 more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ClientPicker
